import React, { useState } from 'react';
import { isBefore, isAfter } from 'date-fns';
//@ts-ignore
import * as styles from './alert-banner.module.css';
import CloseButton from '../Global/CloseButton';
import { graphql } from 'gatsby';

import {
    KontentRichText,
    BrandColor,
    KontentStringValue,
    CTALocation,
} from '../../../types/KontentProps';
import RichText from '../Global/RichText';
import { getColor, getMultiChoice } from '../Global/DataUtils/index';
import CtaLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import AnchorLink from '../Global/AnchorLink';
import { reorderUrl } from '../../global/reorderUrl';

type LinkedAlert = {
    id: number;
    elements: {
        message: KontentRichText;
        bar_color: BrandColor;
        font_color: BrandColor;
        activation_date_time: {
            value: Date;
        };
        expiration_date_time: {
            value: Date;
        };
        cta_configuration__cta_label?: KontentStringValue;
        cta_configuration__cta_location?: CTALocation;
        cta_configuration__cta_location_anchor_point?: KontentStringValue;
        cta_configuration__cta_window_action?: {
            value: {
                codename: 'same_window' | 'new_window';
            }[];
        };
        cta_label?: KontentStringValue;
        cta_location?: CTALocation;
        cta_location_anchor_point?: KontentStringValue;
        cta_window_action?: {
            value: {
                codename: 'same_window' | 'new_window';
            }[];
        };
    };
};
interface Props {
    kontentItemId?: string;
    untitled_linked_items: {
        value: LinkedAlert[];
    };
}

const AlertBanner: React.FC<Props> = ({
    untitled_linked_items,
    kontentItemId,
}) => {
    const [visible, setVisible] = useState(true);
    const [currentDate] = useState<Date>(() => new Date());

    const barColorBkg = getColor(
        untitled_linked_items?.value[0]?.elements?.bar_color
    );
    const barFontColor = getColor(
        untitled_linked_items?.value[0]?.elements?.font_color
    );

    const handleCloseClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        setVisible(false);
    };

    const closeBtnColor = () => {
        if (!visible) return null;
        const visualStyle =
            barColorBkg === '#f5f4f2' || barColorBkg === '#ffffff'
                ? 'dark'
                : 'light';
        return (
            <CloseButton visualStyle={visualStyle} onClick={handleCloseClick} />
        );
    };

    const renderAlerts = (alert: LinkedAlert, alertIdx: number) => {
        let barColor = barColorBkg || '#f06623';
        let fontColor = barFontColor || '#fff';
        const ctaLocationUrl =
            alert?.elements?.cta_configuration__cta_location?.value?.[0]
                ?.elements?.slug?.url ||
            alert?.elements?.cta_configuration__cta_location?.value?.[0]
                ?.elements?.third_party_url?.value ||
            alert?.elements?.cta_configuration__cta_location?.value?.[0]
                ?.elements?.document?.value[0]?.url ||
            alert?.elements?.cta_configuration__cta_location?.value?.[0]
                ?.elements?.file?.value[0]?.url;
        const anchorPointLink =
            alert?.elements?.cta_configuration__cta_location_anchor_point;

        if (
            alert?.elements?.cta_configuration__cta_label?.value ||
            ctaLocationUrl
        ) {
            const ctaWindowAction = getMultiChoice(
                alert.elements.cta_configuration__cta_window_action
            );
            var ctaTarget =
                ctaWindowAction === 'new_window' ? '_blank' : '_self';
            var ctaHref = BuildLinkHref(
                alert?.elements?.cta_configuration__cta_location,
                anchorPointLink
            );
            if (typeof window !== 'undefined' && window.location.search) {
                const hasQuery = ctaHref.includes('?');
                ctaHref += hasQuery
                    ? '&' + window.location.search.substring(1)
                    : window.location.search;
            }
        }

        const alertBannerContent = (
            <div
                className={styles.alert}
                key={alertIdx}
                style={{ backgroundColor: barColor, color: fontColor }}
            >
                {RichText(
                    alert.elements.message,
                    undefined,
                    undefined,
                    undefined,
                    'message'
                )}

                {alert.elements?.cta_configuration__cta_label?.value && (
                    <div className={styles.alertChild}>
                        {alert?.elements.cta_configuration__cta_label?.value ? (
                            <style>{`#alert-cta-link-${alertIdx}:hover { 
                            border-color: ${fontColor} !important; 
                            color: ${barColor} !important;
                            background-color: ${fontColor} !important;
                        }`}</style>
                        ) : (
                            <></>
                        )}
                        {alert?.elements?.cta_configuration__cta_label?.value &&
                        !alert?.elements?.cta_configuration__cta_location?.value
                            ?.length &&
                        anchorPointLink ? (
                            <AnchorLink
                                targetId={anchorPointLink?.value}
                                color={fontColor}
                                hoverBgColor={fontColor}
                                hoverFontColor={barColor}
                            >
                                {
                                    alert?.elements
                                        ?.cta_configuration__cta_label?.value
                                }
                            </AnchorLink>
                        ) : (
                            <CtaLink
                                color={fontColor}
                                hoverBgColor={fontColor}
                                hoverFontColor={barColor}
                                id={`alert-cta-link-${alertIdx}`}
                                href={reorderUrl(ctaHref)}
                                target={ctaTarget}
                            >
                                {
                                    alert.elements?.cta_configuration__cta_label
                                        ?.value
                                }
                            </CtaLink>
                        )}
                    </div>
                )}
            </div>
        );
        return anchorPointLink?.value &&
            typeof window !== 'undefined' &&
            !ctaLocationUrl ? (
            <a
                style={{ width: '100%' }}
                href={`${window.location.pathname}#${anchorPointLink?.value}`}
            >
                {alertBannerContent}
            </a>
        ) : ctaLocationUrl ? (
            <a
                style={{ width: '100%' }}
                href={reorderUrl(ctaHref)}
                target={ctaTarget}
                rel={ctaTarget === '_blank' ? 'noopener noreferrer' : undefined}
            >
                {' '}
                {alertBannerContent}{' '}
            </a>
        ) : (
            alertBannerContent
        );
    };

    return (
        <section className={styles.banner} data-kontent-item-id={kontentItemId}>
            {untitled_linked_items?.value.map((LinkedAlert, alertIdx) => {
                let newAcivationDate = new Date(
                    LinkedAlert.elements.activation_date_time.value
                );
                let newExpirationDate = new Date(
                    LinkedAlert.elements.expiration_date_time.value
                );
                if (
                    (currentDate &&
                        (isBefore(currentDate, newAcivationDate) ||
                            isAfter(currentDate, newExpirationDate))) ||
                    !visible
                ) {
                    return null;
                } else if (!currentDate) return null;
                else {
                    return (
                        <>
                            {renderAlerts(LinkedAlert, alertIdx)}
                            {closeBtnColor()}
                        </>
                    );
                }
            })}
        </section>
    );
};

export default AlertBanner;

export const fragmentAlertBanner = graphql`
    fragment AlertBanner on kontent_item_component___alert_banner {
        elements {
            untitled_linked_items {
                value {
                    ... on kontent_item_content_item___alert_config {
                        id
                        elements {
                            activation_date_time {
                                value
                            }
                            bar_color {
                                ...kontentColor
                            }
                            font_color {
                                ...kontentColor
                            }
                            expiration_date_time {
                                value
                            }
                            message {
                                value
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                }
                            }
                            cta_configuration__cta_location {
                                ...kontentCtaLocation
                            }
                            cta_configuration__cta_label {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
